import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Progress,
  Input,
  Tooltip,
  Pagination,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Button,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  PlayCircleOutlined,
  BookOutlined,
} from "@ant-design/icons";
import LearnerApiService from "../services/LearnerApiService";
import { useNavigate } from "react-router-dom";
import "../styles/MyCourses.css";
import { useDebounceSearch } from "../utils/debounce";

const { Title, Text } = Typography;

const PAGE_LIMIT = 8;

const ellipsisText = (text, max = 32) =>
  text.length > max ? (
    <Tooltip title={text}>{text.slice(0, max)}...</Tooltip>
  ) : (
    text
  );

const LEARN_COURSE_PATH = "/learner/my-courses";

const MyCourses = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(PAGE_LIMIT);
  const [hovered, setHovered] = useState(null);
  const [courses, setCourses] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const abortControllerRef = useRef();

  // Debounce search
  const { debouncedSearch, clearSearchTimeout } = useDebounceSearch(
    (trimmedValue) => {
      setSearch(trimmedValue);
      setPage(1);
    },
    500
  );

  // Fetch courses from API
  const fetchCourses = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    try {
      const { courses: fetchedCourses, pagination: fetchedPagination } =
        await LearnerApiService.getMyCourses({
          page,
          limit,
          search,
        });
      if (!controller.signal.aborted) {
        setCourses(fetchedCourses);
        setPagination(fetchedPagination);
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setCourses([]);
        setPagination({});
      }
    }
    if (!controller.signal.aborted) setLoading(false);
  }, [page, limit, search]);

  useEffect(() => {
    fetchCourses();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      clearSearchTimeout();
    };
  }, [fetchCourses, clearSearchTimeout]);

  // Xử lý search ngay khi Enter hoặc clear input
  const handleSearch = (value) => {
    clearSearchTimeout();
    const trimmedValue = value.trim();
    setSearchInput(value);
    setSearch(trimmedValue);
    setPage(1);
  };

  return (
    <div
      style={{
        background: "#fff",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "32px 48px",
        }}
      >
        <Title level={3} style={{ marginBottom: 16 }}>
          My Courses
        </Title>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search for courses..."
          style={{ maxWidth: 350, marginBottom: 24 }}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            debouncedSearch(e.target.value);
          }}
          onPressEnter={(e) => handleSearch(e.target.value)}
          onClear={() => handleSearch("")}
        />
        {/* <div style={{ marginBottom: 16 }}>
          <Text>
            Display {courses.length} out of {pagination.totalItems || 0} courses
          </Text>
        </div> */}
        <div style={{ minHeight: 480 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <BookOutlined
                style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 16 }}
              />
              <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
                Loading...
              </div>
            </div>
          ) : courses.length === 0 ? (
            <div className="mycourse-empty-state">
              <BookOutlined
                style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 16 }}
              />
              <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
                Course NotFound
              </div>
              <div style={{ color: "#888", fontSize: 16 }}>
                Try searching with different keywords
              </div>
            </div>
          ) : (
            <>
              {/* Responsive: bỏ minHeight và position: absolute cho Row */}
              <div style={{ width: "100%" }}>
                <Row gutter={[32, 32]} style={{ width: "100%" }}>
                  {courses.map((course) => (
                    <Col
                      xs={24}
                      sm={12}
                      md={12}
                      lg={6}
                      xl={6}
                      key={course._id}
                      style={{ display: "flex" }}
                    >
                      <Card
                        hoverable
                        style={{
                          borderRadius: 12,
                          boxShadow: "0 2px 8px #f0f1f2",
                          width: "100%",
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end",
                        }}
                        bodyStyle={{
                          padding: 16,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end",
                        }}
                        onMouseEnter={() => setHovered(course._id)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() =>
                          navigate(`${LEARN_COURSE_PATH}/${course.slug}`)
                        }
                        cover={
                          <div
                            className={`course-cover-img${
                              hovered === course._id ? " hovered" : ""
                            }`}
                            style={{
                              position: "relative",
                              height: 190,
                              overflow: "hidden",
                              borderTopLeftRadius: 12,
                              borderTopRightRadius: 12,
                            }}
                          >
                            <img
                              alt={course.name}
                              src={course.imageUrl}
                              style={{
                                width: "100%",
                                height: 190,
                                objectFit: "cover",
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                              }}
                            />
                            {/* Category Tag  */}
                            <Tag
                              color="blue"
                              style={{
                                position: "absolute",
                                top: 12,
                                right: 5,
                                zIndex: 2,
                                fontWeight: 600,
                                fontSize: 12,
                                color: "#1d39c4",
                                borderRadius: 10,
                                padding: "0px 8px",
                              }}
                            >
                              {course.category?.name}
                            </Tag>

                            {/* Overlay button */}
                            <div
                              className={`course-overlay-fade${
                                hovered === course._id ? " hovered" : ""
                              }`}
                            >
                              <div className="course-overlay-center">
                                <Button
                                  type="default"
                                  icon={
                                    <PlayCircleOutlined
                                      style={{
                                        color: "#222",
                                        fontSize: 18,
                                        marginRight: 8,
                                      }}
                                    />
                                  }
                                  size="middle"
                                  className={`course-overlay-btn${
                                    hovered === course._id ? " hovered" : ""
                                  }`}
                                >
                                  {course.progress?.progressPercentage === 0
                                    ? "Start"
                                    : "Continue"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <Space
                          direction="vertical"
                          size={4}
                          style={{ width: "100%" }}
                        >
                          <Title level={5} style={{ margin: 0 }}>
                            {ellipsisText(course.name, 38)}
                          </Title>
                          <Space size={8} style={{ width: "100%" }}>
                            <UserOutlined style={{ color: "#888" }} />
                            <div
                              style={{
                                maxWidth: 120,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Tooltip title={course.author}>
                                {course.author}
                              </Tooltip>
                            </div>
                          </Space>
                          <Space size={12}>
                            <BookOutlined style={{ color: "#888" }} />
                            <Text>
                              {course.progress?.totalLessons ?? 0} lessons
                            </Text>
                          </Space>
                          <div>
                            <Text strong>Progress</Text>
                            <Progress
                              percent={course.progress?.progressPercentage ?? 0}
                              size="small"
                              strokeColor={
                                course.progress?.progressPercentage >= 80
                                  ? "#52c41a"
                                  : course.progress?.progressPercentage >= 50
                                  ? "#1890ff"
                                  : "#faad14"
                              }
                              style={{ marginBottom: 0 }}
                            />
                          </div>
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
              {/* Pagination */}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 40,
                }}
              >
                <Pagination
                  current={page}
                  pageSize={limit}
                  total={pagination.totalItems || 0}
                  onChange={setPage}
                  showSizeChanger={false}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 8,
                }}
              >
                <Text>Total {pagination.totalItems || 0} courses</Text>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCourses;
